import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { STATUS_FAILED, STATUS_LOADING, STATUS_SUCCEEDED } from '../utils/constants';
import { selectGA, selectStoreName, selectStoreStatus } from '../features/webshop/globalSlice';

const COURSIO_GA_ID = 'G-5TML6LHJGP';

let queuedGaEvents = [];
let trackingReady = false;
let activeTargets = [COURSIO_GA_ID];
let currentStoreName = null;
const configuredIds = new Set();

const normalizeGaTarget = (value) => {
  if (!value) return null;

  const raw = String(value).trim();
  if (!raw) return null;

  // Some stores save values with wrapping quotes or mixed text.
  // Extract first valid GA4 property id from any raw format.
  const matches = raw.match(/G-[A-Z0-9]+/gi);
  if (!matches || !matches.length) return null;

  return matches[0].toUpperCase();
};

const normalizeTargets = (targets = []) => {
  const source = Array.isArray(targets) ? targets : [targets];
  return [...new Set(source.map(normalizeGaTarget).filter(Boolean))];
};

const isValidGaId = (id) => typeof id === 'string' && id.length > 0 && /^G-[A-Z0-9]+$/i.test(id);

const ensureConfig = (gaId) => {
  if (!isValidGaId(gaId) || typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  if (configuredIds.has(gaId)) return;

  window.gtag('config', gaId, { send_page_view: false });
  configuredIds.add(gaId);
};

const buildSendTo = (targets) => {
  const valid = (targets || []).filter(isValidGaId);
  if (!valid.length) return undefined;
  return valid.length === 1 ? valid[0] : valid;
};

const sendGaEvent = (eventName, payload, targets = activeTargets) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const normalizedTargets = normalizeTargets(targets);
  normalizedTargets.forEach(ensureConfig);
  const sendTo = buildSendTo(normalizedTargets);
  if (!sendTo) return;

  const storeName = payload.store_name !== undefined ? payload.store_name : currentStoreName ?? null;
  const { store_name: _dropped, ...restPayload } = payload;
  window.gtag('event', eventName, {
    ...restPayload,
    store_name: storeName,
    send_to: sendTo,
  });
};

const trackEvent = (eventName, payload, targets) => {
  if (targets?.length) {
    sendGaEvent(eventName, payload, targets);
    return;
  }

  if (!trackingReady) {
    queuedGaEvents.push({ eventName, payload });
    return;
  }

  sendGaEvent(eventName, payload);
};

const flushQueuedEvents = () => {
  if (!queuedGaEvents.length) return;

  queuedGaEvents.forEach(({ eventName, payload }) => {
    sendGaEvent(eventName, payload, activeTargets);
  });
  queuedGaEvents = [];
};

const setStoreNameDimension = (storeName) => {
  currentStoreName = storeName ?? null;
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('set', { store_name: currentStoreName });
};

const configureTargets = (targets) => {
  const normalizedTargets = normalizeTargets(targets).filter(isValidGaId);
  normalizedTargets.forEach(ensureConfig);
  activeTargets = normalizedTargets.length ? normalizedTargets : [COURSIO_GA_ID];
  trackingReady = true;
  flushQueuedEvents();
};

export default ({ isStoreRoute = false }) => {
  const ga = useSelector(selectGA);
  const storeName = useSelector(selectStoreName);
  const storeStatus = useSelector(selectStoreStatus);
  const location = useLocation();
  const lastPageRef = useRef('');

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

    ensureConfig(COURSIO_GA_ID);

    if (!isStoreRoute) {
      setStoreNameDimension(null);
      configureTargets([COURSIO_GA_ID]);
      return;
    }

    if (storeStatus === STATUS_LOADING) {
      trackingReady = false;
      queuedGaEvents = [];
      activeTargets = [COURSIO_GA_ID];
      return;
    }

    setStoreNameDimension(storeName);

    const normalizedGaId = normalizeGaTarget(ga?.ga4) || normalizeGaTarget(ga?.id);
    if (normalizedGaId) {
      configureTargets([COURSIO_GA_ID, normalizedGaId]);
      return;
    }

    if (storeStatus === STATUS_SUCCEEDED || storeStatus === STATUS_FAILED) {
      configureTargets([COURSIO_GA_ID]);
    }
  }, [ga, isStoreRoute, storeStatus, storeName]);

  useEffect(() => {
    if (!trackingReady) return;

    const pageKey = `${location.pathname}${location.search}${location.hash}|${activeTargets.join(',')}`;
    if (lastPageRef.current === pageKey) return;
    lastPageRef.current = pageKey;

    sendGaEvent('page_view', {
      page_location: window.location.href,
      page_referrer: document.referrer || '',
    });
  }, [location.hash, location.pathname, location.search]);
};

export const buildTrackingTargets = (ga) => {
  const clientGaId = normalizeGaTarget(ga?.ga4) || normalizeGaTarget(ga?.id);
  return normalizeTargets([COURSIO_GA_ID, clientGaId]);
};

export const viewItemGa = (data, targets) => {
  trackEvent('view_item', { items: [data] }, targets);
};

export const checkoutGa = (data, targets) => {
  trackEvent('begin_checkout', { items: [data] }, targets);
};

export const addItemGa = (data, targets) => {
  trackEvent('add_to_cart', { items: [data] }, targets);
};

export const addTransactionGa = (data, targets) => {
  trackEvent('purchase', data, targets);
};
